import { memo, useCallback } from 'react';
import { PopoverInfo, Switch, cn } from '@divlab/divanui';
import { IconInfoPopup } from '@divlab/divanui/icons';

import { useDeps } from '@Contexts/DI';
import * as Filtrator from '@Stores/Filtrator';
import styles from './Switcher.module.css';

import type { FiltersFilterItem, FiltersParameter, FiltersValue } from '@Types/Filters';
import type { HTMLAttributes } from 'react';

export interface SwitcherProps extends HTMLAttributes<HTMLDivElement> {
  parameter: FiltersParameter;
  data: FiltersValue;
  item: FiltersFilterItem;
  className?: string;
  entityId: Filtrator.FiltratorId;
  notice?: string;
  onToggle?: () => void;
}

const Switcher = (props: SwitcherProps) => {
  const { parameter, data, item, notice, entityId, onToggle, ...restProps } = props;
  const disabled = !data.count;
  const { analytics } = useDeps();

  const handleToggleSwitch = useCallback(() => {
    const opts = { id: data.parameterId, value: data.value };

    if (!!parameter.default) {
      Filtrator.removeCheckbox(entityId, opts);
    } else {
      Filtrator.addCheckbox(entityId, opts);
    }

    if (data.parameterId === 'in_stock')
      analytics.dispatchEvent('constructor.productPage.inStockFilter', {
        action: parameter.default ? 'discharge' : 'apply',
      });

    if (onToggle) onToggle();
  }, [analytics, data.parameterId, data.value, entityId, onToggle, parameter.default]);

  return (
    <div {...restProps} className={cn(styles.switchGroup, { [styles.disabled]: disabled })}>
      <Switch
        disabled={disabled}
        name={item.parameterId}
        checked={!!parameter.default}
        onChange={handleToggleSwitch}
        className={styles.switch}
      />
      <div className={styles.name}>{parameter.name}</div>

      {notice && (
        <PopoverInfo
          preferredPositions={['bottom-left', 'left-bottom']}
          slotTrigger={<IconInfoPopup />}
          className={styles.popoverInfo}
        >
          <div className={styles.popover}>{notice}</div>
        </PopoverInfo>
      )}
    </div>
  );
};

export default memo(Switcher);
